// @flow
import React, { useState, useRef, useEffect } from 'react';
import injectSheet from 'react-jss';
import classNames from 'classnames';
import map from 'lodash/map';
import some from 'lodash/some';
import remove from 'lodash/remove';
import Button from '~/app/components/Button';
import _ from 'shared/lib/gettext';
import { gql, useQuery } from '@apollo/client';
import { ROLE_VALUE_TYPES, OTHERS_ROLE_VALUE } from 'shared/constants';

const capitalize =
  ([first, ...rest]) => first.toUpperCase() + rest.join('').toLowerCase();

const formatOtherRoleName = (category) => {
  if (!category) return '';

  if (category === 'AIXC_DEPLOY') {
    return 'AIXC Deploy';
  }

  return capitalize(category);
};

// Helper function to transform cxc10 paths to AIXC for display
const transformCategoryPath = (path) => {
  if (!path) return path;
  return path.replace(/\/cxc10/gi, '/AIXC');
};

const CATEGORY_TYPE = {
  category: 'category',
  other: 'other'
};

type Category = {
  category: string,
  roleType: string
}

type Props = {
  classes: Object,
  roles: {
    categories: Category[],
    others: Category[],
  },
  onUpdate: Function
};

const RolePanels = (props: Props) => {
  const { classes: c, roles, onUpdate } = props;

  // getCategories
  const { data } = useQuery(QUERY);
  const categories = data?.categories;

  const [leftColumn, setLeftColumn] = useState([]);
  const [rightColumn, setRightColumn] = useState([]);
  const [displayCategories, setDisplayCategories] = useState([]);
  const [displayOthers, setDisplayOthers] = useState([]);

  const leftColumnRef = useRef<HTMLSelectElement>(null);
  const rightColumnRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    if (!categories) {
      return;
    }

    let userCategories = [];

    let userOthers = [];

    if (roles) {
      userCategories = map(roles.categories,
        category => ({ category: category.category, roleType: category.roleType }));
      userOthers = map(roles.others,
        category => ({ category: category.category, roleType: category.roleType }));
    }

    const leftColumn = [];
    const rightColumn = [];
    const displayCategories = [];
    const displayOthers = [];

    /**
     * Loop through each category and combine with each role type.
     * Ex. FF 91 + Editor
     */
    categories.forEach((category) => {
      ROLE_VALUE_TYPES.forEach((type) => {
        const current = { category: category._id, roleType: type };
        const displayName = `${transformCategoryPath(category.path)} ${type.toLowerCase()}`;

        if (some(userCategories, current)) {
          rightColumn.push({
            ...current,
            displayName,
            type: CATEGORY_TYPE.category
          });
        } else {
          leftColumn.push({
            ...current,
            displayName,
            type: CATEGORY_TYPE.category
          });
        }
        displayCategories.push({
          ...current,
          displayName,
          type: CATEGORY_TYPE.category
        });
      });
    });

    /**
     * Loop through each 'others' category and combine with each role type.
     * Ex. Header + Editor
     */
    OTHERS_ROLE_VALUE.forEach((category) => {
      ROLE_VALUE_TYPES.forEach((type) => {
        const current = { category, roleType: type };
        const displayName = `${formatOtherRoleName(category)} ${type.toLowerCase()}`;

        if (some(userOthers, current)) {
          rightColumn.push({
            ...current,
            displayName,
            type: CATEGORY_TYPE.other
          });
        } else {
          leftColumn.push({
            ...current,
            displayName,
            type: CATEGORY_TYPE.other
          });
        }
        displayOthers.push({
          ...current,
          displayName,
          type: CATEGORY_TYPE.other
        });
      });
    });

    setLeftColumn(leftColumn);
    setRightColumn(rightColumn);
    setDisplayCategories(displayCategories);
    setDisplayOthers(displayOthers);
  }, [categories, roles]);

  const setCurrentRoles = (currentRoles) => {
    const roles = [...currentRoles];

    if (!roles.length) {
      onUpdate({
        categories: [],
        others: []
      });
      return;
    }

    // Fallback on enum membership if type is missing or unexpected.
    const categories = roles.filter((c) => {
      const isCategoryType = c.type === CATEGORY_TYPE.category;
      const isOtherType = c.type === CATEGORY_TYPE.other;
      const hasKnownType = isCategoryType || isOtherType;

      return isCategoryType || (!hasKnownType && !OTHERS_ROLE_VALUE.includes(c.category));
    });

    const others = roles.filter((c) => {
      const isCategoryType = c.type === CATEGORY_TYPE.category;
      const isOtherType = c.type === CATEGORY_TYPE.other;
      const hasKnownType = isCategoryType || isOtherType;

      return isOtherType || (!hasKnownType && OTHERS_ROLE_VALUE.includes(c.category));
    });

    // Keeps only attributes that are needed for graphql.
    const categoryRoles = categories.map(
      c => ({ category: c.category, roleType: c.roleType })
    );
    const otherRoles = others.map(
      c => ({ category: c.category, roleType: c.roleType })
    );

    onUpdate({
      categories: categoryRoles,
      others: otherRoles
    });
  }

  const handleClickAdd = () => {
    const selectedColumn = leftColumnRef.current.selectedOptions;
    const selectedColumnArray = [...selectedColumn];

    const tmpLeftColumn = leftColumn.slice();

    // 'removing' is the elements that are moving from left to right column.
    const removing = remove(
      tmpLeftColumn,
      draftCol => selectedColumnArray.some(
        selectedCol => selectedCol.value === draftCol.displayName
      )
    );

    const currentRoles = [
      ...rightColumn,
      ...removing
    ];
    setLeftColumn(tmpLeftColumn);
    setRightColumn(currentRoles);

    setCurrentRoles(currentRoles);
  }

  const handleClickAddAll = () => {
    const currentRoles = [
      ...leftColumn,
      ...rightColumn
    ];
    setLeftColumn([]);
    setRightColumn(x => [
      ...leftColumn,
      ...x
    ]);

    setCurrentRoles(currentRoles);
  }

  const handleClickRemove = () => {
    const selectedColumn = rightColumnRef.current.selectedOptions;
    const selectedColumnArray = [...selectedColumn];

    const tmpRightColumn = rightColumn.slice();

    // 'removing' is the elements that are moving from right to left column.
    const removing = remove(
      tmpRightColumn,
      draftCol => selectedColumnArray.some(
        selectedCol => selectedCol.value === draftCol.displayName
      )
    );

    setLeftColumn(x => [
        ...x,
        ...removing
      ]);
    setRightColumn(tmpRightColumn);

    setCurrentRoles(tmpRightColumn);
  }

  const handleClickRemoveAll = () => {
    setLeftColumn([
      ...displayCategories,
      ...displayOthers
    ])
    setRightColumn([]);

    setCurrentRoles([]);
  }

  return (
    <div className={c.flex}>
      <select
        ref={leftColumnRef}
        multiple="multiple"
        className={
          classNames({
            [c.flexThree]: true,
            [c.selectContainer]: true
          })
        }
      >
        {
          leftColumn.map(
            role => (
              <option
                key={`${role.category}-${role.displayName}`}
                value={role.displayName}
              >
                {role.displayName}
              </option>
            )
          )
        }
      </select>
      <div className={c.selectButtonContainer}>
        <Button className={c.icon} inverted onClick={handleClickAdd}>
          {_('ADD')} <i className="material-icons">chevron_right</i>
        </Button>
        <Button className={c.icon} inverted onClick={handleClickRemove}>
          <i className="material-icons">chevron_left</i> {_('REMOVE')}
        </Button>
        <Button className={c.icon} inverted onClick={handleClickAddAll}>
          {_('ADD_ALL')} <i className="material-icons">last_page</i>
        </Button>
        <Button className={c.icon} inverted onClick={handleClickRemoveAll}>
          <i className="material-icons">first_page</i> {_('REMOVE_ALL')}
        </Button>
      </div>
      <select
        ref={rightColumnRef}
        multiple="multiple"
        className={
          classNames({
            [c.flexThree]: true,
            [c.selectContainer]: true
          })
        }
      >
        {
          rightColumn.map(
            role => (
              <option
                key={`${role.category}-${role.displayName}`}
                value={role.displayName}
              >
                {role.displayName}
              </option>
            )
          )
        }
      </select>
    </div>
  );
}

/* =============================================================================
=    GraphQL
============================================================================= */
const QUERY = gql`
  query getCategories {
    categories {
      _id
      title
      path
      updated
    }
  }
`;

/* =============================================================================
=    CSS
============================================================================= */
const styles = {
  flex: {
    display: 'flex'
  },
  flexThree: {
    flex: 3,
  },
  selectContainer: {
    fontFamily: '"Montserrat", sans-serif',
    height: '260px',
    'overflow-y': 'auto'
  },
  selectButtonContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    padding: '0 20px',
    justifyContent: 'space-evenly'
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
};

export default injectSheet(styles)(RolePanels);
